import { MUIButton, MUICheckBox, MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useDownloadFile } from "../../../../../hooks/useDownloadFile"
import { useFileTypes } from "../../../../../hooks/useFileTypes"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import { FileGet, FilePut } from "../../../../models/File"
import { UserInfo } from "../../../../models/User"

const validationError =
  "Only letters, numbers, underscores (_) and hyphens (-) are allowed"

const validationSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .regex(/^[a-zA-Z0-9_-]+$/, {
      message: validationError,
    }),
  extension: z
    .string()
    .min(1, "Extension is required")
    .regex(/^[a-zA-Z0-9_-]+$/, {
      message: validationError,
    }),
  description: z.string(),
  typeId: z.string().nullable(),
  isPublic: z.boolean({ error: "Is public is required" }),
  quickDownload: z.boolean({ error: "Quick download is required" }),
})

export function FileDetails(props: {
  readonly file: FileGet
  readonly user: UserInfo
  readonly setFile: (updatedFile: FileGet) => void
}) {
  const { file, user, setFile } = props

  const returnURL = `/files`

  const { fileTypes, isLoadingFileTypes } = useFileTypes()

  const { handleDownload, downloading } = useDownloadFile()

  const formMethods = useForm<FilePut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: file.name,
      extension: file.extension,
      description: file.description,
      typeId: file.type ? file.type.id : null,
      isPublic: file.isPublic,
      quickDownload: file.quickDownload,
    })
  }, [file, reset])

  const onSubmit = useCallback(
    (values: FilePut) =>
      digitalManagerApi
        .put<FileGet>(`/files/${file.id}`, {
          name: values.name,
          extension: values.extension,
          description: values.description,
          typeId: values.typeId ? values.typeId : null,
          isPublic: values.isPublic,
          quickDownload: values.quickDownload,
        })
        .then((res) => {
          toast.success("File updated")
          setFile(res.data)
        })
        .catch(() => {
          toast.error("Cannot update file")
          throw new Error("Cannot update file")
        }),
    [file.id, setFile]
  )

  const getFileName = useCallback(
    () =>
      `${file.name}${file.lastVersion.suffix ? file.lastVersion.suffix : ""}.${
        file.extension
      }`,
    [file.name, file.lastVersion.suffix, file.extension]
  )

  const handleDownloadClick = useCallback(() => {
    if (file.downloadLink) {
      handleDownload(file.downloadLink, getFileName())
    }
  }, [handleDownload, file.downloadLink, getFileName])

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<FilePut, "name" | "extension" | "description">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<FilePut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const renderExtensionTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<FilePut, "extension"> }) => (
      <MUITextField
        id="extension"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.extension?.message === "string"
            ? { extension: { message: errors.extension.message } }
            : undefined
        }
      />
    ),
    [errors.extension, handleTextFieldChange]
  )

  const renderDescriptionTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<FilePut, "description"> }) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.description?.message === "string"
            ? { description: { message: errors.description.message } }
            : undefined
        }
      />
    ),
    [errors.description, handleTextFieldChange]
  )

  const handleSelectChange = useCallback(
    (field: ControllerRenderProps<FilePut, "typeId">) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const renderTypeIdSelect = useCallback(
    ({ field }: { field: ControllerRenderProps<FilePut, "typeId"> }) => (
      <MUISelect
        name={field.name}
        id="typeId"
        size={32}
        dropdownLOV={[
          { id: "", name: "" },
          ...fileTypes.map((f) => ({ id: String(f.id), name: f.name ?? "" })),
        ]}
        selectedItem={[field.value ? String(field.value) : ""]}
        onChange={handleSelectChange(field)}
        onBlur={field.onBlur}
        isDisabled={isLoadingFileTypes}
      />
    ),
    [isLoadingFileTypes, fileTypes, handleSelectChange]
  )

  const handleToggle = useCallback(
    (field: ControllerRenderProps<FilePut, "isPublic" | "quickDownload">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderIsPublicCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<FilePut, "isPublic"> }) => (
      <MUICheckBox
        {...field}
        id="isPublic"
        checked={field.value}
        handleToggle={handleToggle(field)}
      />
    ),
    [handleToggle]
  )

  const renderQuickDownloadCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<FilePut, "quickDownload"> }) => (
      <MUICheckBox
        {...field}
        id="quickDownload"
        checked={field.value}
        handleToggle={handleToggle(field)}
      />
    ),
    [handleToggle]
  )

  if (!file) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<FilePut> = {
    formMethods,
    inlineEdit: true,
    canWrite: hasUserAccessToFeature(
      user.authorizations,
      DimaFeatureShortNameEnum.FM,
      AccessLevel.Write
    ),
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{file.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderNameTextField} />
        ),
      },
      {
        id: 1,
        label: "Extension",
        displayItem: <div className="title">{file.extension}</div>,
        editItem: (
          <Controller
            name="extension"
            control={control}
            render={renderExtensionTextField}
          />
        ),
      },
      {
        id: 2,
        label: "Description",
        displayItem: <div className="title">{file.description}</div>,
        editItem: (
          <Controller
            name="description"
            control={control}
            render={renderDescriptionTextField}
          />
        ),
      },
      {
        id: 3,
        label: "Type",
        displayItem: <div className="title">{file.type ? file.type.name : ""}</div>,
        editItem: (
          <Controller name="typeId" control={control} render={renderTypeIdSelect} />
        ),
      },
      {
        id: 4,
        label: "Is Public",
        displayItem: <div className="title">{file.isPublic?.toString() || "false"}</div>,
        editItem: (
          <Controller name="isPublic" control={control} render={renderIsPublicCheckbox} />
        ),
      },
      {
        id: 5,
        label: "Quick Download",
        displayItem: (
          <div className="title">{file.quickDownload?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="quickDownload"
            control={control}
            render={renderQuickDownloadCheckbox}
          />
        ),
      },
      {
        id: 6,
        label: "Owner",
        displayItem: <div className="title">{file.owner.username}</div>,
      },
      {
        id: 7,
        displayItem: (
          <MUIButton
            variant="primary"
            iconName="icon_abb_dias icon-abb_download"
            onClickButton={handleDownloadClick}
            disabled={downloading}
            isLoading={downloading}
            label={downloading ? "Downloading..." : "Download last version"}
            size={32}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
